import { Box, Typography, TextField, Select, MenuItem } from '@mui/material';

const PP = "'Poppins', sans-serif";

// Editable "Form Details (for PDFs)" section on the sale page. Fed by the sale's
// stored FormPayloadJson snapshot (camelCase keys from the RSC/CSC builders).
// Controls mirror the RSC PDF section exactly: Salutation / Insurance Tier /
// Deposit Method / Payment Plan / O&M Warranty are dropdowns with the same
// options; everything else is a text field. Only keys present in the payload are
// rendered (residential vs commercial), and untouched keys are preserved on save.

// Dropdown options copied verbatim from residentialSolarCalculatorV2's PDF section.
const SALUTATIONS = ['MR.', 'MRS.', 'MS.'];
const INSURANCE_TIERS = [
    { value: 10000, label: 'RM 10,000 — FREE' },
    { value: 20000, label: 'RM 20,000 — +RM60' },
    { value: 30000, label: 'RM 30,000 — +RM120' },
    { value: 40000, label: 'RM 40,000 — +RM180' },
    { value: 50000, label: 'RM 50,000 — +RM240' },
];
const DEPOSIT_METHODS = [
    { value: '', label: '— None —' },
    { value: 'Cash', label: 'Cash' },
    { value: 'Bank Transfer', label: 'Bank Transfer' },
    { value: 'Credit Card', label: 'Credit Card' },
];
const PAYMENT_PLANS = [
    { value: 36, label: '36 Months' },
    { value: 60, label: '60 Months' },
];
// Snapshot stores O&M as the formatted string "N year(s)" (see buildResidentialFormPayload).
const OM_WARRANTIES = [
    { value: '1 year', label: '1 Year' },
    { value: '2 years', label: '2 Years' },
    { value: '3 years', label: '3 Years' },
    { value: '4 years', label: '4 Years' },
    { value: '5 years', label: '5 Years' },
];

const SALUTATION_OPTS = SALUTATIONS.map(s => ({ value: s, label: s }));

// Groups: list of { title, fields: [{ key, label, options?, numeric? }] }.
const GROUPS = [
    {
        title: 'Customer / Applicant',
        fields: [
            { key: 'salutation', label: 'Salutation', options: SALUTATION_OPTS },
            { key: 'name', label: 'Name' },
            { key: 'icNumber', label: 'IC Number' },
            { key: 'phone', label: 'Phone' },
            { key: 'email', label: 'Email' },
            { key: 'tinNumber', label: 'TIN Number' },
            { key: 'address', label: 'Address' },
            { key: 'tnbAccountNo', label: 'TNB Account No' },
            { key: 'date', label: 'Date (DD/MM/YYYY)' },
        ],
    },
    {
        title: 'Emergency Contact',
        fields: [
            { key: 'emergencyName', label: 'Name' },
            { key: 'emergencyRelationship', label: 'Relationship' },
            { key: 'emergencyIc', label: 'IC' },
            { key: 'emergencyPhone', label: 'Phone' },
            { key: 'emergencyAddress', label: 'Address' },
            { key: 'emergencyEmail', label: 'Email' },
        ],
    },
    {
        title: 'Company (Commercial)',
        fields: [
            { key: 'companyName', label: 'Company Name' },
            { key: 'companyTin', label: 'Company TIN' },
            { key: 'companyOffice', label: 'Office Phone' },
            { key: 'companyFax', label: 'Fax' },
            { key: 'companyEmail', label: 'Company Email' },
            { key: 'companyWebsite', label: 'Website' },
        ],
    },
    {
        title: 'Person In Charge (Commercial)',
        fields: [
            { key: 'picSalutation', label: 'Salutation', options: SALUTATION_OPTS },
            { key: 'picName', label: 'Name' },
            { key: 'picMykad', label: 'MyKad' },
            { key: 'picPosition', label: 'Position' },
            { key: 'picCitizenship', label: 'Citizenship' },
            { key: 'picMobile', label: 'Mobile' },
            { key: 'picEmail', label: 'Email' },
        ],
    },
    {
        title: 'Alternate Contact (Commercial)',
        fields: [
            { key: 'altSalutation', label: 'Salutation', options: SALUTATION_OPTS },
            { key: 'altName', label: 'Name' },
            { key: 'altMykad', label: 'MyKad' },
            { key: 'altPosition', label: 'Position' },
            { key: 'altRelationship', label: 'Relationship' },
            { key: 'altMobile', label: 'Mobile' },
            { key: 'altEmail', label: 'Email' },
        ],
    },
    {
        title: 'Form Options',
        fields: [
            { key: 'panelWatt', label: 'Panel Watt', readOnly: true },
            { key: 'operationWarrantyYears', label: 'O&M Warranty', options: OM_WARRANTIES },
            { key: 'insuranceAmount', label: 'Insurance Tier', options: INSURANCE_TIERS, numeric: true },
            { key: 'depositPaymentMethod', label: 'Deposit Method', options: DEPOSIT_METHODS, allowEmpty: true },
            // Payment Method + Payment Plan are rendered by a dedicated gated block
            // (Plan disabled on Cash) — see the Payment section below.
        ],
    },
];

// Payment Method options mirror the RSC PDF section (Hidden / Cash / CC / Mix).
const PAYMENT_METHODS = [
    { value: '', label: '— Hidden —' },
    { value: 'Cash', label: 'Cash' },
    { value: 'CC', label: 'CC' },
    { value: 'Mix', label: 'Mix Cash + CC' },
];

// Snapshot stores the derived `paymentMethodLabel` string + numeric
// `quotationPaymentPlan`. Reconstruct the raw method from the label (same strings
// the RSC builder produced) so the dropdown shows correctly on the sale page.
function deriveMethod(label) {
    const s = String(label || '');
    if (s.startsWith('Mix')) return 'Mix';
    if (s.startsWith('CC')) return 'CC';
    if (s === 'Cash') return 'Cash';
    return '';
}
function buildLabel(method, plan) {
    if (method === 'Cash') return 'Cash';
    if (method === 'CC') return `CC (${plan} months)`;
    if (method === 'Mix') return `Mix Cash + CC (${plan} months)`;
    return '';
}

// Don't render the same key twice across groups.
function dedupeFields(groups) {
    const seen = new Set();
    return groups.map(g => ({
        ...g,
        fields: g.fields.filter(f => (seen.has(f.key) ? false : (seen.add(f.key), true))),
    }));
}

const DEDUPED = dedupeFields(GROUPS);

export default function SaleFormDetails({ payload, onChange, canEdit, lockedFields = {} }) {
    if (!payload || typeof payload !== 'object') {
        return (
            <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', fontFamily: PP, fontStyle: 'italic' }}>
                This sale has no stored form snapshot, so there are no PDF form details to edit.
                (Only sales created from the RSC/CSC calculators carry a snapshot.)
            </Typography>
        );
    }

    const labelSx = { fontSize: '0.72rem', color: '#64748B', fontFamily: PP, mb: 0.4 };
    const inputSx = { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' };

    const greyDisabledSx = {
        '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#94A3B8' },
        '& .MuiOutlinedInput-root.Mui-disabled': { bgcolor: '#F1F5F9' },
        '& .MuiFormHelperText-root': { fontFamily: PP, fontSize: '0.6rem', m: 0, mt: 0.25 },
    };

    const renderField = (f) => {
        const val = payload[f.key];
        // Fields already captured in the Customer section above are shown here
        // greyed-out for reference only — the source of truth is up there.
        if (Object.prototype.hasOwnProperty.call(lockedFields, f.key)) {
            return (
                <TextField
                    size="small"
                    fullWidth
                    value={lockedFields[f.key] ?? ''}
                    disabled
                    helperText="From Customer details above"
                    InputProps={{ sx: inputSx }}
                    sx={greyDisabledSx}
                />
            );
        }
        // System specs set by the calculator — display only on the sale page.
        if (f.readOnly) {
            return (
                <TextField
                    size="small"
                    fullWidth
                    value={val ?? ''}
                    disabled
                    helperText="Set by the system configuration"
                    InputProps={{ sx: inputSx }}
                    sx={greyDisabledSx}
                />
            );
        }
        if (f.options) {
            return (
                <Select
                    size="small"
                    fullWidth
                    displayEmpty={!!f.allowEmpty}
                    value={val ?? (f.allowEmpty ? '' : '')}
                    disabled={!canEdit}
                    onChange={(e) => onChange(f.key, f.numeric ? Number(e.target.value) : e.target.value)}
                    sx={inputSx}
                >
                    {f.options.map(o => (
                        <MenuItem key={String(o.value)} value={o.value} sx={{ fontFamily: PP, fontSize: '0.85rem' }}>
                            {o.label}
                        </MenuItem>
                    ))}
                </Select>
            );
        }
        return (
            <TextField
                size="small"
                fullWidth
                value={val ?? ''}
                disabled={!canEdit}
                onChange={(e) => onChange(f.key, e.target.value)}
                InputProps={{ sx: inputSx }}
            />
        );
    };

    return (
        <Box>
            {DEDUPED.map(group => {
                const present = group.fields.filter(f => payload[f.key] !== undefined);
                if (present.length === 0) return null;
                return (
                    <Box key={group.title} sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: PP, mb: 1 }}>
                            {group.title}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                            {present.map(f => (
                                <Box key={f.key}>
                                    <Typography sx={labelSx}>{f.label}</Typography>
                                    {renderField(f)}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                );
            })}

            {/* Payment Method / Payment Plan removed here (deprecated 2026-06-18): the
                main Payment Method dropdown on the sale form is the single source — it
                drives the quotation's CC line + "PAYMENT METHOD" label via the snapshot
                (frozenPricing.paymentMethodLabel / quoteTermMonths). */}
        </Box>
    );
}
